import React, { useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import {
  ADD_UPDATE_COURSE,
  isSchemaMissingError,
  trainingApiAvailability,
  Upload_Document,
} from "../../../../services/userAPI";
import TrainingModuleUploadIcon from "../../../../Images/trainingModuleUploadIcon.png";

const inputClass =
  "h-10 w-full rounded-[8px] border border-[#E3E3E3] bg-white px-3 text-sm outline-none placeholder-[#B5B5B5] focus:border-[#B5B5B5]";

const emptyForm = () => ({
  courseName: "",
  courseDescription: "",
  thumbnailFile: null,
  thumbnailPreview: "",
  publishDate: "",
  points: ["", ""],
});

const AddCourseModule = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ROLE_ID = location.state?.roleId ? parseInt(location.state.roleId) : null;
  const ACCOUNT_ID = location.state?.accountId ? parseInt(location.state.accountId) : null;
  const editing = location.state?.editing || null;

  const [formData, setFormData] = useState(() => {
    if (!editing) return emptyForm();
    return {
      courseName: editing.courseName || editing.title || "",
      courseDescription: editing.courseDescription || editing.description || "",
      thumbnailFile: null,
      thumbnailPreview:
        editing.thumbnailImage_ || editing.thumbnailPreview || editing.thumbnailUrl || "",
      // Raw s3 path (profile_pic/...) — the presigned thumbnailImage_ is for display only
      thumbnailRaw: editing.thumbnailUrl || "",
      publishDate: editing.publishDate?.split("T")[0] || "",
      points: editing.keyPoints?.length
        ? editing.keyPoints.map((k) => k.keyPoint)
        : editing.points?.length
          ? editing.points
          : ["", ""],
    };
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const [addUpdateCourse] = useMutation(ADD_UPDATE_COURSE);
  const [uploadDocument] = useMutation(Upload_Document);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setPoint = (index, value) => {
    setFormData((prev) => {
      const points = [...prev.points];
      points[index] = value;
      return { ...prev, points };
    });
    if (errors.points?.[index])
      setErrors((prev) => ({ ...prev, points: { ...prev.points, [index]: "" } }));
  };

  const addPoint = () => setFormData((prev) => ({ ...prev, points: [...prev.points, ""] }));

  const removePoint = (index) =>
    setFormData((prev) => ({ ...prev, points: prev.points.filter((_, i) => i !== index) }));

  const handleThumbnailPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setField("thumbnailFile", file);
    setField("thumbnailPreview", URL.createObjectURL(file));
    e.target.value = "";
  };

  const removeThumbnail = () => {
    setField("thumbnailFile", null);
    setField("thumbnailPreview", "");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.courseName.trim()) newErrors.courseName = "Course name is required";
    if (!formData.courseDescription.trim())
      newErrors.courseDescription = "Course description is required";
    if (!formData.thumbnailFile && !formData.thumbnailPreview)
      newErrors.thumbnail = "Thumbnail is required";
    if (!formData.publishDate) newErrors.publishDate = "Publish date is required";
    else if (formData.publishDate < new Date().toISOString().split("T")[0])
      newErrors.publishDate = "Publish date cannot be a past date";
    const pointErrors = {};
    formData.points.forEach((p, i) => {
      if (!p.trim()) pointErrors[i] = `Point ${i + 1} is required`;
    });
    if (Object.keys(pointErrors).length) newErrors.points = pointErrors;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveStatically = () => {
    const newCourse = {
      id: editing?.id || Date.now(),
      title: formData.courseName.trim(),
      description: formData.courseDescription.trim(),
      productStatus: "ACTIVE",
      duration: "00:00 Min",
      videoCount: 0,
      language: "",
      publishDate: formData.publishDate,
      thumbnailPreview: formData.thumbnailPreview,
      keyPoints: formData.points
        .map((p) => ({ keyPoint: p.trim() }))
        .filter((p) => p.keyPoint),
      points: formData.points.map((p) => p.trim()).filter(Boolean),
    };
    message.success(editing ? "Module updated successfully!" : "Module added successfully!");
    setIsSubmitting(false);
    navigate("/clan-management/training", {
      state: { ...location.state, newCourse },
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Skip the API entirely once the schema says it doesn't exist
    if (!trainingApiAvailability.addUpdateCourse) {
      saveStatically();
      return;
    }
    try {
      // Edit: resend the short stored path — never the presigned URL (>255 chars)
      let thumbnailUrl = formData.thumbnailRaw || "";
      if (formData.thumbnailFile) {
        const res = await uploadDocument({
          variables: { s3folder: "COMPANY_LOGO", document: formData.thumbnailFile },
          context: { useMultipart: true },
        });
        if (!res?.data?.uploadDocument?.success) {
          throw new Error(res?.data?.uploadDocument?.message || "Thumbnail upload failed");
        }
        thumbnailUrl = res.data.uploadDocument?.data?.data?.filename || "";
      }

      const keyPoints = formData.points
        .map((p) => ({ keyPoint: p.trim() }))
        .filter((p) => p.keyPoint);

      const result = await addUpdateCourse({
        variables: {
          input: {
            accountId: ACCOUNT_ID,
            jobroleId: ROLE_ID,
            courseName: formData.courseName.trim(),
            courseDescription: formData.courseDescription.trim(),
            thumbnailUrl,
            publishDate: formData.publishDate,
            keyPoints,
            ...(editing?.id ? { id: editing.id } : {}),
          },
        },
      });
      if (result?.data?.addUpdateCourse?.success === false) {
        throw new Error(result.data.addUpdateCourse.message || "Save failed");
      }
      const saved = result?.data?.addUpdateCourse?.data;
      const courseId = saved?.id;

      if (!courseId) {
        saveStatically();
        return;
      }

      message.success(editing ? "Module updated successfully!" : "Module added successfully!");
      setIsSubmitting(false);
      // Redirect back to the list — getCourseDetails fires there on mount
      navigate("/clan-management/training", {
        state: { ...location.state },
      });
    } catch (err) {
      if (isSchemaMissingError(err)) {
        trainingApiAvailability.addUpdateCourse = false;
        saveStatically();
        return;
      }
      message.error("Save failed: " + err.message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="px-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 font-['Inter'] text-[13px] font-medium text-[#AD0101] transition-colors hover:text-[#8C0101]"
      >
        <ArrowLeft size={14} strokeWidth={2.5} /> Back
      </button>

      <h1 className="mt-3 font-['Inter'] text-[20px] font-semibold leading-[100%] text-[#1F2020]">
        {editing ? "Edit Course Module" : "Add Course Module"}
      </h1>

      {/* Course Details */}
      <div className="mt-5 rounded-[16px] border border-[#EDEDED] bg-white p-6">
        <h2 className="font-['Inter'] text-[20px] font-semibold leading-[100%] text-[#1F2020]">Course Details</h2>

        <div className="mt-4">
          <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
            Course Name <span className="text-[#AD0101]">*</span>
          </label>
          <input
            type="text"
            value={formData.courseName}
            onChange={(e) => setField("courseName", e.target.value)}
            className={inputClass}
            placeholder="Enter Course Name"
          />
          {errors.courseName && (
            <p className="mt-1 text-xs text-[#AD0101]">{errors.courseName}</p>
          )}
        </div>

        <div className="mt-4">
          <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
            Course Description<span className="text-[#AD0101]">*</span>
          </label>
          <textarea
            value={formData.courseDescription}
            onChange={(e) => setField("courseDescription", e.target.value)}
            rows={4}
            className="w-full resize-none rounded-[8px] border border-[#E3E3E3] bg-white px-3 py-2 text-sm outline-none placeholder-[#B5B5B5] focus:border-[#B5B5B5]"
            placeholder="Enter Course Description"
          />
          {errors.courseDescription && (
            <p className="mt-1 text-xs text-[#AD0101]">{errors.courseDescription}</p>
          )}
        </div>

        <div className="mt-4">
          <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
            Upload Thumbnail<span className="text-[#AD0101]">*</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleThumbnailPick}
          />
          <div className="relative w-fit">
            {formData.thumbnailPreview ? (
              <div className="relative flex h-[145px] w-[315px] items-center justify-center rounded-[8px] border border-[#E3E3E3] bg-white p-4">
                <img
                  src={formData.thumbnailPreview}
                  alt="Thumbnail"
                  className="max-h-full max-w-full object-contain"
                />
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className="absolute right-2 top-2 text-[#888888] transition-colors hover:text-[#AD0101]"
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-[145px] w-[315px] flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed border-[#BDBDBD] bg-white transition-colors"
              >
                <img
                  src={TrainingModuleUploadIcon}
                  alt=""
                  className="h-[82px] w-[72px] object-contain"
                />
                {/* <span className="font-['Inter'] text-[11px] text-[#888888]">Choose File</span> */}
              </button>
            )}
          </div>
          {errors.thumbnail && (
            <p className="mt-1 text-xs text-[#AD0101]">{errors.thumbnail}</p>
          )}
        </div>

        <div className="mt-4 w-[220px]">
          <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
            Publish Date<span className="text-[#AD0101]">*</span>
          </label>
          <input
            type="date"
            value={formData.publishDate}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => setField("publishDate", e.target.value)}
            className={inputClass}
          />
          {errors.publishDate && (
            <p className="mt-1 text-xs text-[#AD0101]">{errors.publishDate}</p>
          )}
        </div>
      </div>

      {/* What you will learn */}
      <div className="mt-4 rounded-[16px] border border-[#EDEDED] bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-['Inter'] text-[18px] font-semibold leading-[100%] tracking-[-0.36px] text-[#1F2020]">
            What you will learn <span className="text-[#AD0101]">*</span>
          </h2>
          <button
            type="button"
            onClick={addPoint}
            className="flex items-center gap-1 font-['Inter'] text-[13px] font-semibold text-[#AD0101] transition-colors hover:text-[#8C0101]"
          >
            <Plus size={14} strokeWidth={2.5} /> Add Point
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-4">
          {formData.points.map((point, index) => (
            <div key={index}>
              <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
                Point {index + 1}
                <span className="text-[#AD0101]">*</span>
              </label>
              <div className="relative w-full">
                <input
                  type="text"
                  value={point}
                  onChange={(e) => setPoint(index, e.target.value)}
                  className={`${inputClass} ${formData.points.length > 2 ? "pr-9" : ""}`}
                  placeholder={`Enter Point ${index + 1}`}
                />
                {formData.points.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removePoint(index)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#AD0101] transition-colors"
                    title="Remove point"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              {errors.points?.[index] && (
                <p className="mt-1 text-xs text-[#AD0101]">{errors.points[index]}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer buttons */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-[8px] border border-[#E3E3E3] bg-white px-6 py-2 font-['Inter'] text-[14px] font-medium text-[#1F2020] transition-colors hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="rounded-[8px] bg-[#AD0101] px-6 py-2 font-['Inter'] text-[14px] font-semibold text-white transition-colors hover:bg-[#8C0101] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default AddCourseModule;
